import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { RoutePaths } from "constants/RoutePaths";
import { useAuthContext } from "hooks/useAuthContext";
import { useGetTags } from "hooks/useGetTags";
import { useGetTrainingWithProgress } from "hooks/useGetTrainingWithProgress";
import { useGetLessonImage } from "hooks/useGetLessonImage";
import { GameEditor } from "components/views/editor/gameEditor/GameEditor";
import { useTrainingPageData } from "hooks/useTrainingPageData";
import { useTrainingPageCallbacks } from "hooks/useTrainingPageCallbacks";
import { useEditorUnsavedChangesGuard } from "hooks/useEditorUnsavedChangesGuard";
import { useExportHandlers } from "hooks/useExportHandlers";
import { useGameEditorValidator } from "hooks/useGameEditorValidator";
import { Image } from "components/views/editor/models/Image";
import { findAnyVersionByLanguage, getInitialVersion } from "components/views/editor/helpers/versionUtils";
import { buildLessonForm, buildGameEditorData } from "components/views/editor/helpers/lessonFormHelper";
import { useUpdateGameLesson } from "hooks/useUpdateGameLesson";
import { UpdateLessonPageLayout } from "components/views/editor/UpdateLessonPageLayout";
import { IGetLessonWithVersionsResponse } from "api/models/response/IGetLessonWithVersionsResponse";
import { LessonType } from "models/LessonType";
import { GameLessonVersion, LessonVersion } from "models/LessonVersion";

interface UpdateGameLessonPageProps {
    lessonData: IGetLessonWithVersionsResponse;
    fetchLessonWithLatestVersions: () => Promise<unknown>;
}

export const UpdateGameLessonPage = ({ lessonData, fetchLessonWithLatestVersions }: UpdateGameLessonPageProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { onWorkspaceChanged } = useAuthContext();

    const [currentLanguage, setCurrentLanguage] = useState<string>("");
    const [currentVersion, setCurrentVersion] = useState<LessonVersion | null>(null);
    const [lessonImage, setLessonImage] = useState<Image | null>(null);
    const [pendingNewVersion, setPendingNewVersion] = useState<string | null>(null);

    const {
        lessonFormData,
        setLessonFormData,
        setEditor,
        gameEditorData,
        setGameEditorData,
        tags,
        isLessonFormValid,
        isNodeEditorValid,
        resetFormErrors,
        formValidationErrors
    } = useTrainingPageData();

    const { fetchTags } = useGetTags();
    const { fetchLessonImage, loading: fetchImagePending } = useGetLessonImage();
    const { fetchTrainingWithProgress, fetchTrainingWithProgressPending } = useGetTrainingWithProgress();
    const { updateGameLesson, isUpdating } = useUpdateGameLesson();
    const { isValid: isGameEditorValid } = useGameEditorValidator();

    const { handleFormDataChange, onGameEditorChange } = useTrainingPageCallbacks({
        setLessonFormData,
        setEditor,
        setGameEditorData,
        formValidationErrors,
        resetFormErrors
    });

    const {
        isDialogOpen,
        guardedAction,
        handleCancelDialog,
        handleConfirmDialog,
        setBlockerActive
    } = useEditorUnsavedChangesGuard({
        editor: undefined,
        savedConfig: undefined
    });

    const { handleExportClick, handleExportToVrClick } = useExportHandlers({
        lessonData,
        lessonFormData,
        editor: null,
        isLessonFormValid,
        isNodeEditorValid: () => true,
        fetchTrainingWithProgress,
        fetchTrainingWithProgressPending
    });

    const isCurrentVersionNew = pendingNewVersion === currentLanguage;

    const switchLanguage = useCallback(
        (language: string) => {
            if (!lessonData) return;

            const version = findAnyVersionByLanguage(lessonData.versions, language);

            if (!version) {
                setCurrentLanguage(language);
                const pendingVersion: GameLessonVersion = {
                    type: LessonType.GAME,
                    id: "",
                    lessonId: lessonData.lessonId,
                    lessonName: lessonData.lessonName,
                    maxPoints: 0,
                    version: 0,
                    createdAt: "",
                    language,
                    name: "",
                    description: null,
                    launchParams: "",
                    fileId: "",
                    events: [],
                    isDefault: false
                };
                setCurrentVersion(pendingVersion);
                setLessonFormData(buildLessonForm(lessonData, pendingVersion, lessonImage));
                setGameEditorData({ fileId: null, launchParams: "", events: [] });
                return;
            }

            setCurrentLanguage(language);
            setCurrentVersion(version);
            setLessonFormData(buildLessonForm(lessonData, version, lessonImage));
            setGameEditorData(buildGameEditorData(version as GameLessonVersion));
        },
        [lessonData, lessonImage, setLessonFormData, setGameEditorData]
    );

    const handleLanguageChange = useCallback(
        (language: string) => {
            guardedAction(() => switchLanguage(language));
        },
        [guardedAction, switchLanguage]
    );

    const handleRefresh = useCallback(() => {
        guardedAction(fetchLessonWithLatestVersions);
    }, [guardedAction, fetchLessonWithLatestVersions]);

    const handleAddNewVersion = useCallback(
        (language: string) => {
            if (pendingNewVersion !== null) {
                guardedAction(() => {
                    setPendingNewVersion(language);
                    switchLanguage(language);
                });
            } else {
                setPendingNewVersion(language);
                guardedAction(() => switchLanguage(language));
            }
        },
        [pendingNewVersion, guardedAction, switchLanguage]
    );

    const handleSaveClick = async () => {
        if (!lessonData || !lessonFormData || !currentVersion) return;
        if (!isLessonFormValid(lessonFormData)) return;
        if (!isGameEditorValid(gameEditorData)) return;

        const success = await updateGameLesson(
            lessonData.lessonId,
            lessonFormData,
            currentLanguage,
            gameEditorData
        );

        if (!success) return;

        if (isCurrentVersionNew) {
            setPendingNewVersion(null);
        }

        await fetchTags();
        await fetchLessonWithLatestVersions();

        enqueueSnackbar(t("lessonEditor.saveSuccess"), { variant: "success" });
    };

    useEffect(() => {
        if (!lessonData || lessonData.versions.length === 0) return;
        if (lessonData.photoFileId && lessonImage === null && fetchImagePending) return;

        const userLanguage = i18n.language || "en";
        const versionToLoad = getInitialVersion(lessonData.versions, userLanguage);

        if (!versionToLoad) return;

        setCurrentLanguage(versionToLoad.language);
        setCurrentVersion(versionToLoad);
        setLessonFormData(buildLessonForm(lessonData, versionToLoad, lessonImage));
        setGameEditorData(buildGameEditorData(versionToLoad as GameLessonVersion));
    }, [lessonData, lessonImage, fetchImagePending, setLessonFormData, setGameEditorData]);

    useEffect(() => {
        const loadLessonImage = async () => {
            if (!lessonData?.photoFileId) {
                setLessonImage(null);
                return;
            }

            const image = await fetchLessonImage(lessonData.photoFileId);
            setLessonImage(image);
        };

        loadLessonImage();
    }, [lessonData?.photoFileId, fetchLessonImage]);

    useEffect(() => {
        const handleWorkspaceChange = () => {
            setBlockerActive(false);
            navigate(RoutePaths.EDITOR);
        };

        const unsubscribe = onWorkspaceChanged(handleWorkspaceChange);
        return () => unsubscribe();
    }, [navigate, onWorkspaceChanged, setBlockerActive]);

    if (!lessonFormData || !tags || !currentVersion) {
        return null;
    }

    return (
        <UpdateLessonPageLayout
            lessonData={lessonData}
            lessonFormData={lessonFormData}
            tags={tags}
            formValidationErrors={formValidationErrors}
            currentLanguage={currentLanguage}
            pendingNewVersion={pendingNewVersion}
            onFormDataChange={handleFormDataChange}
            onLanguageChange={handleLanguageChange}
            onRefresh={handleRefresh}
            onAddNewVersion={handleAddNewVersion}
            onSave={handleSaveClick}
            isUpdating={isUpdating}
            isDialogOpen={isDialogOpen}
            onCancelDialog={handleCancelDialog}
            onConfirmDialog={handleConfirmDialog}
            fetchTrainingWithProgressPending={fetchTrainingWithProgressPending}
            onExportToVrClick={handleExportToVrClick}
            onExportClick={handleExportClick}
            showImportButton={false}
            showExportFileButton={false}
        >
            <GameEditor
                data={gameEditorData}
                maxPoints={lessonFormData.maxPoints}
                onChange={onGameEditorChange}
            />
        </UpdateLessonPageLayout>
    );
};
